import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  ChecklistTemplateAdminSummary,
  ChecklistTemplateUpdateBody,
} from "@bitacorapro/api-types";
import {
  queryKeys,
  useChecklistTemplateAssets,
  useFacilityDashboard,
  useRecipe,
  useUpdateChecklistTemplate,
  useUpdateChecklistTemplateAssets,
} from "../lib/api/queries";
import { ApiError } from "../lib/api/error";
import { isFacilityDashboardEnabled } from "../lib/capabilities";
import { effectiveBrandId } from "../lib/util";
import { focusHeading, useDialogFocus } from "../lib/useDialogFocus";
import { useQueryView } from "../lib/loading/useQueryView";
import { PageError } from "./PageError";
import { PageSkeleton } from "./PageSkeleton";
import { StorePicker, type StorePickerItem } from "./StorePicker";
import { StorePickerSkel } from "./skeleton-compositions/StorePickerSkel";
import { Icon } from "./icons";

type Step = "select" | "confirm";

// What Panel B is confirming — drives its copy, its CTA label, and which
// of the two mutation hooks fires. "edit" also covers the degenerate
// case of a pure scope-unchanged, zero-asset-delta submit, but that can
// never reach here: the "Revisar cambios" button is disabled whenever
// nothing changed (see hasChanges below), so Panel B always has a real
// diff to show once it's reached.
type ConfirmKind = "edit" | "brandToAssets" | "assetsToBrand";

// checklistTemplateUpdateBody's `assetIds` is a branded AssetId[] (core-engine
// assetIdSchema) that api-types doesn't export as a standalone type. Every id
// this component handles is a plain string (StorePicker, useState, the
// looser PUT-assets mutation) — this alias exists only to cast selectedIds
// back to the branded shape at the one PATCH call site that requires it.
type BrandedAssetIds = NonNullable<ChecklistTemplateUpdateBody["assetIds"]>;

function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

// Two-panel assignment drawer for one template (#0424, ADR-0079). Content
// swaps between a selection step and a confirmation step inside a single
// open dialog — never two routes, never a re-mount — so useDialogFocus
// only needs to run its open sequence once; focusHeading (below) handles
// every subsequent Panel A <-> Panel B swap.
export function ChecklistTemplateAssignDrawer({
  template,
  onClose,
  onSaved,
}: {
  template: ChecklistTemplateAdminSummary;
  onClose: () => void;
  /** Fired after a successful save, before the drawer unmounts — lets
   *  the panel show its own "Asignación actualizada" toast (the drawer
   *  itself is gone by the time a toast would render). */
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const { data: recipe } = useRecipe();
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const dashboardQuery = useFacilityDashboard(facilityEnabled);
  const dashboard = dashboardQuery.data;
  const assetsQuery = useChecklistTemplateAssets(template.id);
  const { data: assetsData, refetch: refetchAssets } = assetsQuery;
  // Two independent loads behind the store area (loading contract): the
  // persisted assignment (always) and the candidate stores (facility
  // only). A disabled dashboard query is "nothing on its way", never a
  // skeleton that doesn't end.
  const assetsView = useQueryView(assetsQuery);
  const dashboardView = useQueryView(dashboardQuery);

  const assetsMutation = useUpdateChecklistTemplateAssets();
  const templateMutation = useUpdateChecklistTemplate();

  const [step, setStep] = useState<Step>("select");
  const [scope, setScope] = useState<"brand" | "assets">(template.scope);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<{ code?: string; message: string } | null>(null);

  const drawerRef = useRef<HTMLElement>(null);
  const panelAHeadingRef = useRef<HTMLHeadingElement>(null);
  const panelBHeadingRef = useRef<HTMLHeadingElement>(null);
  const seededRef = useRef(false);
  const skipFocusSwapRef = useRef(true);

  useDialogFocus(true, drawerRef);

  // Seed the working selection from the persisted set exactly once —
  // refetchOnMount: "always" on useChecklistTemplateAssets means this
  // could in principle re-fire, but the guard here means a background
  // refetch never clobbers an in-progress edit.
  useEffect(() => {
    if (seededRef.current || !assetsData) return;
    seededRef.current = true;
    setSelectedIds(assetsData.assets.map((a) => a.id));
  }, [assetsData]);

  // Move focus to whichever panel's heading just became current — but
  // not on the initial mount, where useDialogFocus already owns the
  // first-focus decision (the first focusable control, not the heading).
  useEffect(() => {
    if (skipFocusSwapRef.current) {
      skipFocusSwapRef.current = false;
      return;
    }
    focusHeading(step === "confirm" ? panelBHeadingRef : panelAHeadingRef);
  }, [step]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Widened to plain `string` on the way out — checklistTemplateAssetsResponse's
  // `assets[].id` is a branded AssetId (core-engine assetIdSchema), but every
  // consumer here (StorePicker, Set/array equality, the PUT-assets mutation's
  // looser `assetIds: string[]` param) works in plain strings. Only the PATCH
  // scope-flip body needs the branded shape back, cast at that one call site.
  const originalIds = useMemo(
    (): string[] => assetsData?.assets.map((a) => a.id) ?? [],
    [assetsData]
  );

  const groups = dashboard?.byAssetGroup ?? [];
  const candidateAssets = dashboard?.byAsset ?? [];
  const groupNameById = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups]);

  // The picker's candidate list, pre-filtered to the template's own
  // brand — this is what makes CHECKLIST_TEMPLATE_BRAND_MISMATCH
  // structurally unreachable rather than a caught-after-PUT surprise.
  // Null brandId (brand-agnostic template) shows every org store,
  // mirroring the backend's own early-return in
  // assertAssetsBelongToOrgAndBrand.
  const stores: StorePickerItem[] = useMemo(() => {
    const filtered =
      template.brandId === null
        ? candidateAssets
        : candidateAssets.filter((a) => effectiveBrandId(a, groups) === template.brandId);
    return filtered.map((a) => ({
      id: a.id,
      unit: a.unit,
      address: a.address,
      assetGroupName: a.assetGroupId ? (groupNameById.get(a.assetGroupId) ?? null) : null,
    }));
  }, [candidateAssets, groups, groupNameById, template.brandId]);

  // Resolved for the assets -> brand warning copy ("...toda la marca
  // {brandName}..."). Neither ChecklistTemplateAdminSummary nor the
  // facility-dashboard schemas index brand rows by id directly, so this
  // reads whichever asset/group row already carries the name.
  const brandName = useMemo(() => {
    if (!template.brandId) return null;
    const fromAsset = candidateAssets.find((a) => a.brandId === template.brandId)?.brandName;
    if (fromAsset) return fromAsset;
    return groups.find((g) => g.brandId === template.brandId)?.brandName ?? null;
  }, [candidateAssets, groups, template.brandId]);

  // Store names survive outside the brand-filtered `stores` list too —
  // a persisted assignment whose asset fell out of the current brand
  // filter (a brand reassignment elsewhere) still needs a name in the
  // "se quitará" list.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stores) map.set(s.id, s.unit ?? s.address ?? s.id);
    for (const a of assetsData?.assets ?? []) {
      if (!map.has(a.id)) map.set(a.id, a.unit ?? a.address ?? a.id);
    }
    return map;
  }, [stores, assetsData]);
  const nameFor = (id: string) => nameById.get(id) ?? id.slice(0, 8);

  const scopeChanged = scope !== template.scope;
  const idsChanged = !sameIdSet(selectedIds, originalIds);
  const hasChanges = scopeChanged || idsChanged;
  const isBrandToAssetsFlip = scopeChanged && template.scope === "brand" && scope === "assets";
  const isAssetsToBrandFlip = scopeChanged && template.scope === "assets" && scope === "brand";
  const confirmKind: ConfirmKind = isBrandToAssetsFlip
    ? "brandToAssets"
    : isAssetsToBrandFlip
      ? "assetsToBrand"
      : "edit";

  const assetsFailed = assetsView.kind === "error";
  const storesLoading =
    assetsView.kind === "loading" || (facilityEnabled && dashboardView.kind === "loading");
  // The skeleton paints only past the shared delay (a fast load never
  // flashes); until then its frame is mounted hidden, reserving the space.
  const storesSkelVisible =
    (assetsView.kind === "loading" && assetsView.visible) ||
    (dashboardView.kind === "loading" && dashboardView.visible);
  const isSaving = assetsMutation.isPending || templateMutation.isPending;
  const reviewDisabled =
    assetsFailed ||
    storesLoading ||
    isSaving ||
    !hasChanges ||
    (isBrandToAssetsFlip && selectedIds.length === 0);

  function onScopeChange(next: "brand" | "assets") {
    // "Nothing pre-checked" on a fresh brand -> assets flip (ADR-0079
    // synthesis copy) — every time the toggle *leaves* brand for assets,
    // not only on the template's original baseline, so a bounce
    // brand -> assets -> brand -> assets doesn't resurrect a stale
    // in-memory pick the admin never confirmed.
    if (next === "assets" && scope === "brand") {
      setSelectedIds([]);
    }
    setScope(next);
  }

  const added = selectedIds.filter((id) => !originalIds.includes(id));
  const removed = originalIds.filter((id) => !selectedIds.includes(id));
  const wouldEmptyAssetsScope =
    confirmKind === "edit" && scope === "assets" && selectedIds.length === 0;

  async function handleConfirm() {
    setSubmitError(null);
    try {
      if (confirmKind === "edit") {
        await assetsMutation.mutateAsync({ templateId: template.id, assetIds: selectedIds });
      } else if (confirmKind === "brandToAssets") {
        await templateMutation.mutateAsync({
          templateId: template.id,
          body: { scope: "assets", assetIds: selectedIds as BrandedAssetIds },
        });
      } else {
        await templateMutation.mutateAsync({
          templateId: template.id,
          body: { scope: "brand" },
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.code === "CHECKLIST_TEMPLATE_BRAND_MISMATCH") {
        setSubmitError({
          code: apiErr.code,
          message:
            "No pudimos guardar: una o más tiendas seleccionadas ya no pertenecen a la marca de este checklist. Volvemos a cargar la lista de tiendas — revisa tu selección.",
        });
        setStep("select");
        void refetchAssets();
        // useFacilityDashboard has no manual refetch surface exposed
        // here beyond invalidation — the candidate list is refreshed by
        // invalidating the shared dashboard query the next screen visit
        // reads, same posture other admin writes in this app take.
        void qc.invalidateQueries({ queryKey: queryKeys.facilityDashboard });
      } else if (apiErr?.code === "CHECKLIST_TEMPLATE_SCOPE_EMPTY") {
        setSubmitError({
          code: apiErr.code,
          message:
            "Selecciona al menos una tienda para activar el checklist en modo «Tiendas específicas».",
        });
      } else {
        setSubmitError({
          message: "No pudimos guardar los cambios de asignación. Intenta de nuevo.",
        });
      }
    }
  }

  const errorId = "checklist-assign-error";
  const confirmCtaLabel =
    confirmKind === "assetsToBrand" ? "Confirmar cambio de alcance" : "Confirmar y guardar";

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} role="presentation" />
      <aside
        ref={drawerRef}
        className="drawer"
        role={step === "confirm" && confirmKind === "assetsToBrand" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={
          step === "confirm" ? "checklist-assign-panel-b-h" : "checklist-assign-panel-a-h"
        }
      >
        <div className="drawer__head">
          <div className="drawer__title">Asignar tiendas</div>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="Cerrar">
            <Icon.Close />
          </button>
        </div>

        <div className="drawer__body">
          {step === "select" ? (
            <>
              <h2
                id="checklist-assign-panel-a-h"
                ref={panelAHeadingRef}
                tabIndex={-1}
                className="drawer__obs-title"
              >
                {template.label}
              </h2>

              {assetsView.kind === "error" ? (
                // Can't safely render a scope/picker form without knowing
                // the persisted baseline — every diff downstream (kept/
                // added/removed, the no-op guard) depends on originalIds.
                // A section of the drawer: the error never moves focus.
                <PageError
                  title="No pudimos cargar las tiendas asignadas a este checklist."
                  description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
                  onRetry={assetsView.retry}
                  autoFocus={false}
                />
              ) : (
                <>
                  <fieldset
                    className="field"
                    disabled={isSaving}
                    aria-describedby={submitError ? errorId : undefined}
                  >
                    <legend className="field__label">Alcance del checklist</legend>
                    <label className="drawer__notify">
                      <input
                        type="radio"
                        name="scope"
                        checked={scope === "brand"}
                        onChange={() => onScopeChange("brand")}
                      />
                      <span>Toda la marca</span>
                    </label>
                    <label className="drawer__notify">
                      <input
                        type="radio"
                        name="scope"
                        checked={scope === "assets"}
                        onChange={() => onScopeChange("assets")}
                      />
                      <span>Tiendas específicas</span>
                    </label>
                  </fieldset>

                  {submitError && (
                    <p id={errorId} className="auth-error" role="alert">
                      {submitError.message}
                    </p>
                  )}

                  {storesLoading ? (
                    <PageSkeleton
                      label="Cargando tiendas…"
                      visible={storesSkelVisible}
                      announce={false}
                      kind="section"
                    >
                      <StorePickerSkel />
                    </PageSkeleton>
                  ) : scope === "assets" ? (
                    <StorePicker
                      stores={stores}
                      selectedIds={selectedIds}
                      onChange={setSelectedIds}
                      disabled={isSaving}
                      originalIds={originalIds}
                    />
                  ) : isAssetsToBrandFlip ? (
                    <>
                      <div className="notice-banner">
                        <Icon.Warning s={14} />{" "}
                        {brandName
                          ? `Este checklist va a aplicarse a todas las tiendas de la marca ${brandName}, no solo a las ${originalIds.length} que tiene asignadas hoy.`
                          : `Este checklist va a aplicarse a todas las tiendas de la marca, no solo a las ${originalIds.length} que tiene asignadas hoy.`}
                      </div>
                      <StorePicker
                        stores={stores}
                        selectedIds={selectedIds}
                        onChange={() => {}}
                        disabled
                        originalIds={originalIds}
                      />
                    </>
                  ) : (
                    <p className="cell-sub">
                      Este checklist aplica a todas las tiendas de la marca. Cambia el alcance para
                      elegir tiendas específicas.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <h2
                id="checklist-assign-panel-b-h"
                ref={panelBHeadingRef}
                tabIndex={-1}
                className="drawer__obs-title"
              >
                {confirmKind === "assetsToBrand"
                  ? "Confirmar cambio de alcance"
                  : "Revisar cambios"}
              </h2>

              {submitError && (
                <p id={errorId} className="auth-error" role="alert">
                  {submitError.message}
                </p>
              )}

              {confirmKind === "brandToAssets" && (
                <>
                  <p className="cell-sub">
                    Cambiando de «Toda la marca» a «Tiendas específicas». Este checklist dejará de
                    aplicarse a todas las tiendas y quedará disponible solo en las tiendas
                    seleccionadas:
                  </p>
                  <ul className="checklist-detail-items">
                    {selectedIds.map((id) => (
                      <li key={id} className="checklist-detail-item">
                        <span>{nameFor(id)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {confirmKind === "assetsToBrand" && (
                <div className="notice-banner">
                  <Icon.Warning s={14} />{" "}
                  {brandName
                    ? `Este checklist va a aplicarse a todas las tiendas de la marca ${brandName}, no solo a las ${originalIds.length} que tiene asignadas hoy.`
                    : `Este checklist va a aplicarse a todas las tiendas de la marca, no solo a las ${originalIds.length} que tiene asignadas hoy.`}
                </div>
              )}

              {confirmKind === "edit" && (
                <>
                  {added.length > 0 && (
                    <>
                      <p className="field__label">Se agregará a {added.length} tienda(s)</p>
                      <ul className="checklist-detail-items">
                        {added.map((id) => (
                          <li key={id} className="checklist-detail-item">
                            <span>{nameFor(id)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {wouldEmptyAssetsScope ? (
                    <div className="notice-banner">
                      <Icon.Warning s={14} /> Este checklist quedará sin ninguna tienda asignada y
                      no aparecerá en ningún local hasta que asignes al menos una.
                    </div>
                  ) : (
                    removed.length > 0 && (
                      <>
                        <p className="field__label">Se quitará de {removed.length} tienda(s)</p>
                        <ul className="checklist-detail-items">
                          {removed.map((id) => (
                            <li key={id} className="checklist-detail-item">
                              <span>{nameFor(id)}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="drawer__foot">
          {step === "select" ? (
            <>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={reviewDisabled}
                onClick={() => setStep("confirm")}
              >
                Revisar cambios
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setStep("select")}
                disabled={isSaving}
              >
                Volver a editar
              </button>
              {submitError ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={isSaving}
                  onClick={() => void handleConfirm()}
                >
                  Reintentar
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={isSaving}
                  onClick={() => void handleConfirm()}
                >
                  {isSaving ? "Guardando…" : confirmCtaLabel}
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
